import { colors } from '@/theme/foundations/colors';
import { lazy, Suspense } from 'react';
import { Box, Icon, Flex, useDisclosure } from '@chakra-ui/react';
import {
  Folder,
  LayersThree02,
  Plus,
  Send01,
  UserCircle,
} from '@kula-ai/icons-core';
import { KMenuButton } from '@/ui-library/components';
import { KText } from '@/ui-library/components';
import { useCreateJob } from '@/api/jobs/useJobs';
import { useDispatch } from 'react-redux';
import { fetchProjects } from '@/store/projects/projectsActions';
import { projectsActions } from '@/store/projects';
import { contactsListActions } from '@/store/contactsList';
import { useSelector } from 'react-redux';
import { getContactsList } from '@/store/contactsList/contactsListSelectors';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';

// The top nav renders on every page, so these four forms are fetched only when
// one is opened — imported statically they pulled most of the ATS code into
// the entry bundle.
const CreateContact = lazy(() =>
  import('@/components/ats/CreateContact/CreateContact').then((m) => ({
    default: m.CreateContact,
  }))
);
const JobCreateEditModal = lazy(() =>
  import('@/components/shared/jobs/JobCreateEditModal').then((m) => ({
    default: m.JobCreateEditModal,
  }))
);
const CreateProjectDrawer = lazy(() =>
  import('@/components/shared/project-modal/create-project-drawer').then(
    (m) => ({ default: m.CreateProjectDrawer })
  )
);
const CreateOutreach = lazy(() =>
  import('@/pages/mco/components/create').then((m) => ({
    default: m.CreateOutreach,
  }))
);

export const QuickCreateOptions = () => {
  const createContactActions = useDisclosure();
  const createJobActions = useDisclosure();
  const createProjectActions = useDisclosure();
  const createOutreachActions = useDisclosure();
  const dispatch = useDispatch();
  const { mutateAsync: createJob } = useCreateJob();
  const { applySort, clearAllFilter, applySearchTerm } = projectsActions;
  const { filters = {} } = useSelector(getContactsList);
  const {
    contactsPermissions,
    isJobCreateAllowed,
    flowsPermissions: { createFlow },
  } = useAtsPermissions();

  if (!isJobCreateAllowed && !createFlow && !contactsPermissions?.create) {
    return null;
  }

  const createJobConfig = [
    {
      id: 'createJob' as const,
      label: 'Start from scratch',
      description: 'Create a job with a blank canvas',
      modalActionText: 'Create',
      loadingText: 'Creating job',
      testID: 'create-job-from-scratch-button',
      apiCall: (payload) => createJob(payload),
    },
  ];

  const createOptions = [
    {
      id: '1',
      label: 'Add candidate',
      description: 'Create new candidate profile',
      icon: UserCircle,
      background: 'pastel-3-light',
      iconColor: 'pastel-3-dark',
      onClick: () => {
        createContactActions.onOpen();
      },
      enabled: contactsPermissions?.create,
    },
    {
      id: '2',
      label: 'Add job',
      description: 'Create new job posting and requirements',
      icon: LayersThree02,
      background: 'pastel-4-light',
      iconColor: 'pastel-4-dark',
      onClick: () => {
        createJobActions.onOpen();
      },
      enabled: isJobCreateAllowed,
    },
    {
      id: '3',
      label: 'Add project',
      description: 'Build talent pipeline for future roles',
      icon: Folder,
      background: 'pastel-5-light',
      iconColor: 'pastel-5-dark',
      onClick: () => {
        createProjectActions.onOpen();
      },
      enabled: createFlow,
    },
    {
      id: '4',
      label: 'Add flow',
      description: 'Create automated sequence workflow',
      icon: Send01,
      background: 'pastel-6-light',
      iconColor: 'pastel-6-dark',
      onClick: () => {
        createOutreachActions.onOpen();
      },
      enabled: createFlow,
    },
  ];

  const fetchProjectsInBg = () => {
    dispatch(applySearchTerm({ searchTerm: '' }));
    dispatch(applySort({ sortOption: 'created_at', order: 'desc' }));
    dispatch(clearAllFilter());
    dispatch(
      fetchProjects({
        searchTerm: '',
        filters: {},
        sortOption: {},
        page: {},
      })
    );
  };

  const renderTriggerContent = () => (
    <Flex
      // The top bar is navy in both themes, so the button keeps its light look.
      bg="static-white"
      borderRadius="full"
      w="24px"
      h="24px"
      alignItems="center"
      justifyContent="center"
    >
      {/* Always dark: the disc is static-white in both modes. */}
      <Plus color={colors['secondary-900']} size={20} />
    </Flex>
  );

  const renderMenuContent = () => {
    const enabledOptions = createOptions.filter((option) => option.enabled);
    return (
      <Box
        width="380px"
        bg="white"
        borderRadius="sm"
        border="1px solid"
        borderColor="border-color"
      >
        {enabledOptions.map((item, index) => (
          <Box key={item?.id}>
            <Flex
              p="12px"
              pb={'12px'}
              cursor="pointer"
              onClick={item?.onClick}
              align="flex-start"
              borderBottom={
                index < enabledOptions.length - 1 ? '1px solid' : 'none'
              }
              borderColor="secondary-100"
              _hover={{
                bg: 'secondary-50',
                borderRadius: index === 0 ? '6px 6px 0 0' : '0px',
              }}
              transition="background-color 0.2s"
              gap="12px"
            >
              <Box
                background={item.background}
                borderRadius="6px"
                p="6px"
                w="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={item.icon} color={item.iconColor} w="16px" h="16px" />
              </Box>

              <Flex direction="column" align="flex-start" flex={1} gap="2px">
                <KText size="sm-medium" color="secondary-900">
                  {item?.label}
                </KText>
                <KText size="xs" color="secondary-700">
                  {item?.description}
                </KText>
              </Flex>
            </Flex>
          </Box>
        ))}
      </Box>
    );
  };

  const refetchContacts = () => {
    dispatch(
      contactsListActions?.applyFilter({
        value: !filters?.updatePage,
        key: 'updatePage',
      })
    );
  };

  return (
    <>
      <KMenuButton
        variant="quaternary"
        triggerContent={renderTriggerContent()}
        menuContent={renderMenuContent()}
        showRightIcon={false}
        menuContentProps={{
          padding: '0px !important',
        }}
        p="0px !important"
        minW="20px"
        h="24px"
        border="none"
        bg="none"
        mr="4px"
        _hover={{ bg: 'none' }}
        _active={{ bg: 'none' }}
      />
      <Suspense fallback={null}>
        {createContactActions?.isOpen && (
          <CreateContact
            modalActions={createContactActions}
            refetch={
              window?.location?.pathname.includes('candidates')
                ? refetchContacts
                : undefined
            }
            redirectToContactOnCreate
          />
        )}
        {createJobActions?.isOpen && (
          <JobCreateEditModal
            modalActions={createJobActions}
            config={createJobConfig[0]}
          />
        )}

        {createProjectActions?.isOpen && (
          <CreateProjectDrawer
            modalActions={createProjectActions}
            fetchProjectsInBg={fetchProjectsInBg}
          />
        )}
        {createOutreachActions?.isOpen && (
          <CreateOutreach
            isOpen={createOutreachActions?.isOpen}
            onClose={createOutreachActions?.onClose}
            quickCreate={true}
          />
        )}
      </Suspense>
    </>
  );
};
